import axios, { AxiosError } from "axios";

import { sanitizeForLogging } from "./sanitizeForLogging";

/**
 * Custom error class for API errors with response data
 * Used when API responses don't match expected format
 */
export class ApiResponseError extends Error {
  readonly responseData?: unknown;
  readonly responseStatus?: number;

  constructor(message: string, responseData?: unknown, responseStatus?: number) {
    super(message);
    this.name = "ApiResponseError";
    this.responseData = responseData;
    this.responseStatus = responseStatus;
  }
}

/**
 * Checks if a status code is a client error (4xx) or server error (5xx)
 */
const isHttpErrorStatus = (status: number): boolean => {
  return status >= 400 && status < 600;
};

/**
 * Logs Axios errors with sanitized request and response data
 * Only logs HTTP errors (4xx/5xx) - skips network errors as they're not actionable
 */
const logAxiosError = (
  method: string,
  path: string,
  endpoint: string,
  requestData: unknown,
  requestDataKey: "requestPayload" | "queryParams",
  error: AxiosError,
): void => {
  if (error.response) {
    const status = error.response.status;
    // Only log HTTP errors (4xx/5xx) - skip network errors
    if (isHttpErrorStatus(status)) {
      console.error(`[API Error] ${method} ${path} failed with ${status} error`, {
        endpoint,
        [requestDataKey]: sanitizeForLogging(requestData),
        responseData: sanitizeForLogging(error.response.data),
        responseStatus: status,
      });
    }
  }
};

/**
 * Logs non-Axios errors (e.g., manual throws for unexpected response formats)
 */
const logNonAxiosError = (
  method: string,
  path: string,
  endpoint: string,
  requestData: unknown,
  requestDataKey: "requestPayload" | "queryParams",
  error: Error,
): void => {
  // Check if it's our custom ApiResponseError with response data
  if (error instanceof ApiResponseError) {
    console.error(`[API Error] ${method} ${path} failed with unexpected response format`, {
      endpoint,
      [requestDataKey]: sanitizeForLogging(requestData),
      ...(error.responseData !== undefined && {
        responseData: sanitizeForLogging(error.responseData),
      }),
      ...(error.responseStatus !== undefined && {
        responseStatus: error.responseStatus,
      }),
      errorMessage: error.message,
    });
  } else {
    // Generic error without response data
    console.error(`[API Error] ${method} ${path} failed with unexpected error`, {
      endpoint,
      [requestDataKey]: sanitizeForLogging(requestData),
      errorMessage: error.message,
    });
  }
};

/**
 * Logs API errors with sanitized request and response data
 * Handles both Axios errors and non-Axios errors (e.g., manual throws)
 * Automatically extracts response data from error objects if present
 * @param method - HTTP method (GET, POST, PUT, DELETE, etc.)
 * @param path - API path that was called
 * @param endpoint - Full resolved endpoint URL
 * @param requestData - Request payload (body for POST/PUT, query params for GET)
 * @param requestDataKey - Key name for request data in logs ("requestPayload" for POST, "queryParams" for GET)
 * @param error - The error that occurred (Axios error or regular Error)
 */
export const logApiError = (
  method: string,
  path: string,
  endpoint: string,
  requestData: unknown,
  requestDataKey: "requestPayload" | "queryParams",
  error: unknown,
): void => {
  if (axios.isAxiosError(error)) {
    logAxiosError(method, path, endpoint, requestData, requestDataKey, error);
  } else if (error instanceof Error) {
    logNonAxiosError(method, path, endpoint, requestData, requestDataKey, error);
  }
};
